import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import BelegAnsichtModal from './BelegAnsichtModal';
import LaufProtokoll from './LaufProtokoll';
import BelegKarte from './BelegKarte';
import { BelegKarte as BelegKarteDaten } from '../types';
import { belegZiel, istFreigabeAufgabe } from '../lib/freigabeAufgabe';
import { kannZuAuftragNavigieren, navigiereZuAuftrag } from '../lib/navigation';
import './AufgabenOverview.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

interface Freigabe {
  freigabe_id: number;
  aktion_key: string;
  zusammenfassung: string | null;
  entitaet_typ: string | null;
  entitaet_id: number | null;
  lauf_id: number | null;
  /** Protokoll-Konversation des Laufs (für Prüfer ohne Admin-Recht direkt lesbar). */
  konversation_id: number | null;
  status: string;
}

interface Aufgabe {
  aufgabe_id: number;
  entitaet_typ: string;
  entitaet_id: number;
  titel: string;
  beschreibung: string | null;
  faellig_am: string | null;
  zugewiesen_an: number | null;
  status: 'Offen' | 'In Arbeit' | 'Erledigt';
  erstellt_von: number | null;
  erstellt_am: string | null;
  erledigt_am: string | null;
}

interface AufgabenOverviewProps {
  authToken: string;
}

function formatDatum(iso: string | null): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function getFaelligKlasse(faellig_am: string | null, status: string): string {
  if (!faellig_am || status === 'Erledigt') return '';
  const faellig = new Date(faellig_am);
  const jetzt = new Date();
  const heute = new Date(jetzt.getFullYear(), jetzt.getMonth(), jetzt.getDate());
  const faelligTag = new Date(faellig.getFullYear(), faellig.getMonth(), faellig.getDate());
  if (faelligTag < heute) return 'ueberfaellig';
  if (faelligTag.getTime() === heute.getTime()) return 'heute';
  return '';
}

const STATUS_COLOR: Record<string, string> = {
  'Offen': 'var(--gold)',
  'In Arbeit': 'var(--green-900)',
  'Erledigt': 'var(--green-500)',
};

const AufgabenOverview: React.FC<AufgabenOverviewProps> = ({ authToken }) => {
  const [aufgaben, setAufgaben] = useState<Aufgabe[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<'alle' | 'Offen' | 'In Arbeit' | 'Erledigt'>('Offen');
  const [freigaben, setFreigaben] = useState<Record<number, Freigabe>>({});
  const [freigabenFehler, setFreigabenFehler] = useState(false);
  const [inFlight, setInFlight] = useState<Record<number, boolean>>({});
  const inFlightRef = useRef<Set<number>>(new Set());
  const [hinweis, setHinweis] = useState('');
  const [wartet, setWartet] = useState<BelegKarteDaten[]>([]);
  const [wartetFehler, setWartetFehler] = useState(false);
  const [protokollAufgabe, setProtokollAufgabe] = useState<number | null>(null);
  const [belegAnsicht, setBelegAnsicht] = useState<{ url: string; title: string } | null>(null);

  const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    // „Wartet auf dich“: eigener Abruf, ein Fehler hier darf die Aufgabenliste nicht stören.
    try {
      const w = await axios.get(`${API_URL}/api/wartet-auf-dich`, { headers });
      setWartet((w.data?.eintraege ?? []) as BelegKarteDaten[]);
      setWartetFehler(false);
    } catch {
      setWartet([]);
      setWartetFehler(true);
    }
    try {
      const url = `${API_URL}/api/aufgaben/meine`;
      const res = await axios.get(url, { headers });
      setAufgaben(res.data);
      if ((res.data as Aufgabe[]).some(istFreigabeAufgabe)) {
        try {
          const fr = await axios.get(`${API_URL}/api/freigaben`, { headers });
          setFreigaben(Object.fromEntries((fr.data as Freigabe[]).map(f => [f.freigabe_id, f])));
          setFreigabenFehler(false);
        } catch {
          setFreigaben({});
          setFreigabenFehler(true);
        }
      } else {
        setFreigaben({});
        setFreigabenFehler(false);
      }
    } catch {
      setError('Aufgaben konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (aufgabeId: number, status: string) => {
    try {
      await axios.put(
        `${API_URL}/api/aufgaben/${aufgabeId}/status`,
        { status },
        { headers }
      );
      load();
    } catch {
      setError('Status konnte nicht aktualisiert werden.');
    }
  };

  /** Entscheidung mit Doppelklick-Schutz je Freigabe (Ref sperrt sofort, State deaktiviert die Buttons). */
  const entscheiden = async (
    freigabeId: number,
    pfad: 'genehmigen' | 'ablehnen',
    body: Record<string, unknown>,
    erfolg: string,
    fehlerText: string
  ) => {
    if (inFlightRef.current.has(freigabeId)) return;
    inFlightRef.current.add(freigabeId);
    setInFlight(prev => ({ ...prev, [freigabeId]: true }));
    setError('');
    setHinweis('');
    try {
      await axios.post(`${API_URL}/api/freigaben/${freigabeId}/${pfad}`, body, { headers });
      setError('');
      setHinweis(erfolg);
      await load();
    } catch (e: any) {
      setHinweis('');
      setError(e?.response?.data?.detail || fehlerText);
    } finally {
      inFlightRef.current.delete(freigabeId);
      setInFlight(prev => ({ ...prev, [freigabeId]: false }));
    }
  };

  const genehmigen = (freigabeId: number) =>
    entscheiden(freigabeId, 'genehmigen', {}, 'Freigegeben, Mitarbeiter arbeitet weiter', 'Freigabe konnte nicht erteilt werden.');

  const ablehnen = (freigabeId: number) => {
    if (inFlightRef.current.has(freigabeId)) return;
    const grund = window.prompt('Grund für die Ablehnung:');
    if (grund === null) return;
    return entscheiden(freigabeId, 'ablehnen', { grund: grund.trim() }, 'Abgelehnt', 'Freigabe konnte nicht abgelehnt werden.');
  };

  const gefilterteAufgaben =statusFilter === 'alle'
    ? aufgaben
    : aufgaben.filter(a => a.status === statusFilter);

  const offen = aufgaben.filter(a => a.status === 'Offen').length;
  const inArbeit = aufgaben.filter(a => a.status === 'In Arbeit').length;
  const erledigt = aufgaben.filter(a => a.status === 'Erledigt').length;

  return (
    <div className="ao-container">
      <div className="ao-header">
        <h2 className="ao-title">Aufgaben</h2>
        <button className="ao-refresh-btn" onClick={load} disabled={loading}>
          {loading ? '⏳' : '↻'} Aktualisieren
        </button>
      </div>

      {/* Statistik-Kacheln */}
      <div className="ao-stats">
        <div className="ao-stat-card offen" onClick={() => setStatusFilter('Offen')}>
          <span className="ao-stat-zahl">{offen}</span>
          <span className="ao-stat-label">Offen</span>
        </div>
        <div className="ao-stat-card in-arbeit" onClick={() => setStatusFilter('In Arbeit')}>
          <span className="ao-stat-zahl">{inArbeit}</span>
          <span className="ao-stat-label">In Arbeit</span>
        </div>
        <div className="ao-stat-card erledigt" onClick={() => setStatusFilter('Erledigt')}>
          <span className="ao-stat-zahl">{erledigt}</span>
          <span className="ao-stat-label">Erledigt</span>
        </div>
      </div>

      {/* Filter-Zeile */}
      <div className="ao-filter-row">
        <div className="ao-filter-group">
          {(['alle', 'Offen', 'In Arbeit', 'Erledigt'] as const).map(s => (
            <button
              key={s}
              className={`ao-filter-btn ${statusFilter === s ? 'active' : ''}`}
              onClick={() => setStatusFilter(s)}
            >
              {s === 'alle' ? 'Alle' : s}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="ao-error">{error}</div>}
      {hinweis && <div className="ao-hinweis">{hinweis}</div>}

      {wartetFehler && (
        <div className="ao-hinweis">„Wartet auf dich“ konnte nicht geladen werden.</div>
      )}
      {wartet.length > 0 && (
        <div className="ao-wartet">
          <h3 className="ao-wartet-titel">Wartet auf dich ({wartet.length})</h3>
          {wartet.map(k => (
            <BelegKarte key={`${k.typ}-${k.id}`} karte={k} onChange={() => load()} />
          ))}
        </div>
      )}

      {loading ? (
        <div className="ao-loading">Aufgaben werden geladen…</div>
      ) : gefilterteAufgaben.length === 0 ? (
        <div className="ao-empty">
          
          <p>Keine Aufgaben für diesen Filter vorhanden.</p>
        </div>
      ) : (
        <div className="ao-list">
          {gefilterteAufgaben.map(a => {
            const faelligKlasse = getFaelligKlasse(a.faellig_am, a.status);
            const freigabe = istFreigabeAufgabe(a) ? freigaben[a.entitaet_id] : undefined;
            const ziel = freigabe ? belegZiel(freigabe.entitaet_typ, freigabe.entitaet_id) : null;
            return (
              <div
                key={a.aufgabe_id}
                className={`ao-item ${a.status === 'Erledigt' ? 'erledigt' : ''} ${faelligKlasse}`}
              >
                <div className="ao-item-header">
                  <span className="ao-titel">{a.titel}</span>
                  <span
                    className="ao-status-badge"
                    style={{ background: STATUS_COLOR[a.status] || 'var(--ink-2)' }}
                  >
                    {a.status}
                  </span>
                </div>
                {a.beschreibung && (
                  <div className="ao-beschreibung">{a.beschreibung}</div>
                )}
                {istFreigabeAufgabe(a) && !freigabe && a.status !== 'Erledigt' && (
                  <div className="ao-freigabe-text">
                    {freigabenFehler
                      ? 'Freigaben konnten nicht geladen werden.'
                      : 'Freigabe nicht mehr offen oder nicht berechtigt.'}
                  </div>
                )}
                {freigabe && (
                  <div className="ao-freigabe">
                    {freigabe.zusammenfassung && (
                      <div className="ao-freigabe-text">{freigabe.zusammenfassung}</div>
                    )}
                    <div className="ao-actions">
                      {ziel?.art === 'auftrag' && kannZuAuftragNavigieren() && (
                        <button className="ao-btn small secondary" onClick={() => navigiereZuAuftrag(ziel.id)}>
                          Zum Auftrag
                        </button>
                      )}
                      {ziel?.art === 'ausgangsrechnung' && (
                        <button
                          className="ao-btn small secondary"
                          onClick={() => setBelegAnsicht({ url: `${API_URL}${ziel.url}`, title: `Rechnung #${ziel.id}` })}
                        >
                          Zum Beleg
                        </button>
                      )}
                      {freigabe.konversation_id != null && (
                        <button
                          className="ao-btn small secondary"
                          onClick={() => setProtokollAufgabe(protokollAufgabe === a.aufgabe_id ? null : a.aufgabe_id)}
                        >
                          Protokoll
                        </button>
                      )}
                      <button
                        className="ao-btn small success"
                        disabled={!!inFlight[freigabe.freigabe_id]}
                        onClick={() => genehmigen(freigabe.freigabe_id)}
                      >
                        Genehmigen
                      </button>
                      <button
                        className="ao-btn small"
                        disabled={!!inFlight[freigabe.freigabe_id]}
                        onClick={() => ablehnen(freigabe.freigabe_id)}
                      >
                        Ablehnen
                      </button>
                    </div>
                    {protokollAufgabe === a.aufgabe_id && freigabe.konversation_id != null && (
                      <LaufProtokoll
                        authToken={authToken}
                        konversationId={freigabe.konversation_id}
                        onClose={() => setProtokollAufgabe(null)}
                      />
                    )}
                  </div>
                )}
                <div className="ao-item-footer">
                  <div className="ao-meta">
                    <span className="ao-entitaet">
                      {a.entitaet_typ} #{a.entitaet_id}
                    </span>
                    {a.faellig_am && (
                      <span className={`ao-faellig ${faelligKlasse}`}>
                        {faelligKlasse === 'ueberfaellig' ? 'Überfällig: ' : faelligKlasse === 'heute' ? 'Heute: ' : 'Fällig: '}
                        {formatDatum(a.faellig_am)}
                      </span>
                    )}
                  </div>
                  <div className="ao-actions">
                    {a.status !== 'Offen' && (
                      <button className="ao-btn small" onClick={() => updateStatus(a.aufgabe_id, 'Offen')}>
                        Offen
                      </button>
                    )}
                    {!freigabe && a.status !== 'In Arbeit' && (
                      <button className="ao-btn small secondary" onClick={() => updateStatus(a.aufgabe_id, 'In Arbeit')}>
                        In Arbeit
                      </button>
                    )}
                    {!freigabe && a.status !== 'Erledigt' && (
                      <button className="ao-btn small success" onClick={() => updateStatus(a.aufgabe_id, 'Erledigt')}>
                        ✓ Erledigt
                      </button>
                    )}
                  </div>
                </div>
                {a.erledigt_am && (
                  <div className="ao-erledigt-am">Erledigt am: {formatDatum(a.erledigt_am)}</div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {belegAnsicht && (
        <BelegAnsichtModal
          url={belegAnsicht.url}
          title={belegAnsicht.title}
          kontext={{}}
          onClose={() => setBelegAnsicht(null)}
        />
      )}
    </div>
  );
};

export default AufgabenOverview;
